import { AtMostOne } from '@kindo/universal';
import { useState } from 'react';

import { ButtonType } from '../Button';
import { TooltipPlacement } from '../ToolTip/ToolTip';

import SelectOptionComponent from './SelectOption';

import { TextIconColor } from '~/components/core/constants/colors.consts';
import { ButtonSize, Size } from '~/components/core/constants/size.consts';
import { Dropdown } from '~/components/core/Dropdown';
import { Icon } from '~/components/core/Icon';
import { TextField } from '~/components/core/TextField';
import { TextFieldType } from '~/components/core/TextField/TextField.consts';
import { ToolTip } from '~/components/core/ToolTip';
import { Typography, TypographySize } from '~/components/core/Typography';

export interface SelectOptionType<Value extends string | null = string> {
  label: string;
  value: Value;
  description?: string;
  destructive?: boolean;
  disabled?: boolean;
  endIcon?: { icon: Icon; color?: TextIconColor };
  onClick?: (value: Value) => void;
  prefix?: string;
  startIcon?: { icon: Icon; color?: TextIconColor };
  suffix?: { text: string; color?: TextIconColor; size?: TypographySize };
  tooltip?: string;
}

interface BaseSelectProps<Value extends string | null = string> {
  onChange: (value: Value) => void;
  options: SelectOptionType<Value>[];
  placeholderLabel: string;
  value: Value | null;
  allowInput?: boolean;
  buttonSize?: ButtonSize;
  className?: string;
  'data-id'?: string | undefined;
  disabled?: boolean;
  hideChevron?: boolean;
  highlightIfNull?: boolean;
  inputPlaceholder?: string;
  prefix?: string;
  reservedValues?: string[];
  searchable?: boolean;
  startIcon?: Icon | undefined;
  tooltip?: string | undefined;
  type?: ButtonType;
  typographySize?: TypographySize;
}

export type SelectProps<Value extends string | null = string> = AtMostOne<
  BaseSelectProps<Value>,
  'allowInput' | 'searchable'
>;

const Select = <Value extends string | null = string>({
  onChange,
  options,
  value,
  placeholderLabel,
  allowInput = false,
  'data-id': dataId,
  disabled,
  hideChevron = false,
  highlightIfNull = false,
  inputPlaceholder,
  startIcon,
  prefix,
  reservedValues,
  searchable = false,
  type = ButtonType.OUTLINED,
  tooltip,
  buttonSize = Size.MEDIUM,
  typographySize,
  className
}: SelectProps<Value>) => {
  const [inputValue, setInputValue] = useState<string>('');
  const [searchValue, setSearchValue] = useState<string>('');

  const selectedOption = options.find((option) => option.value === value);

  const filteredOptions =
    searchable && searchValue.trim()
      ? options.filter((option) =>
          option.label.toLowerCase().includes(searchValue.trim().toLowerCase())
        )
      : options;

  // If the value was typed as the input value and does not exist as an option,
  // use the typed value as the label
  const label =
    value && !selectedOption
      ? value
      : selectedOption?.label || placeholderLabel;

  const handleClickSelect = (newValue: Value) => {
    setInputValue('');
    setSearchValue('');
    onChange(newValue);
  };

  const handleSearchChange = (value: string) => {
    setSearchValue(value);
  };

  const handleInputUpdate = () => {
    // If Enter is pressed and new values are allowed, select the input value
    if (allowInput && !reservedValues?.includes(inputValue)) {
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      onChange(inputValue as Value);
    }
  };

  const handleDropdownClose = () => {
    handleInputUpdate();
    setSearchValue('');
  };

  const buttonType =
    highlightIfNull && value === null ? ButtonType.OUTLINED_COLOR : type;

  return (
    <Dropdown
      // The dropdown will automatically close when a SelectOption is clicked
      // because SelectOption uses role="menuitem" and closeOnClick is true
      closeOnClick
      data-testid="select-dropdown"
      disabled={!!disabled}
      onClickOutside={handleDropdownClose}
      trigger={{
        buttonProps: {
          'data-id': dataId,
          label: (prefix ?? '') + label,
          startIcon,
          tooltip: tooltip ?? '',
          typographySize,
          type: buttonType,
          fullWidth: true,
          className,
          size: buttonSize
        },
        hideChevron
      }}
    >
      {searchable && (
        <div className="p-1 px-2" data-testid="select-search-container">
          <TextField
            autoFocus
            fullWidth
            onChange={handleSearchChange}
            placeholder="Search..."
            size={TypographySize.SMALL}
            type={TextFieldType.SIMPLE}
            value={searchValue}
          />
        </div>
      )}
      {allowInput && (
        <div className="p-1 px-2" data-testid="select-input-container">
          <TextField
            autoFocus
            fullWidth
            onBlur={handleInputUpdate}
            onChange={(v) => setInputValue(v)}
            onEnter={handleInputUpdate}
            placeholder={inputPlaceholder ?? ''}
            size={TypographySize.SMALL}
            type={TextFieldType.SIMPLE}
            value={inputValue}
          />
        </div>
      )}
      {filteredOptions.length === 0 && searchable && searchValue.trim() ? (
        <div className="p-2">
          <Typography
            color={TextIconColor.SECONDARY}
            size={TypographySize.SMALL}
          >
            No results found
          </Typography>
        </div>
      ) : (
        filteredOptions.map((option) => (
          <ToolTip
            content={option.tooltip ?? ''}
            key={option.value}
            offset={[0, 8]}
            placement={TooltipPlacement.RIGHT}
          >
            <SelectOptionComponent<Value>
              description={option.description}
              destructive={option.destructive ?? false}
              disabled={option.disabled ?? false}
              endIcon={option.endIcon}
              isSelected={option.value === value}
              key={option.value}
              label={option.label}
              onClick={option.onClick ?? handleClickSelect}
              prefix={option.prefix ?? ''}
              startIcon={option.startIcon}
              suffix={option.suffix ?? { text: '' }}
              value={option.value}
            />
          </ToolTip>
        ))
      )}
    </Dropdown>
  );
};

export default Select;
